import React, { useState, useEffect, useRef } from "react";
import { gsap } from "gsap";
import Calendar from "react-calendar";
import { bookingConfig } from "../../../../../data/booking/bookingConfig";
import { format, addDays, isBefore, startOfDay } from "date-fns";
import "react-calendar/dist/Calendar.css";
import "./DateSelector.css";

const DateSelector = ({ onSelectDate }) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const selectorRef = useRef(null);
  const headerRef = useRef(null);
  const calendarRef = useRef(null);
  const selectedRef = useRef(null);

  const today = startOfDay(new Date());
  const maxDate = addDays(today, bookingConfig.maxDaysInAdvance);

  // Initial fade-in animation
  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline();

      tl.from(headerRef.current, {
        opacity: 0,
        y: -30,
        duration: 0.6,
        ease: "power3.out",
      }).from(
        calendarRef.current,
        {
          opacity: 0,
          y: 40,
          scale: 0.95,
          duration: 0.8,
          ease: "power3.out",
        },
        "-=0.3",
      );
    }, selectorRef);

    return () => ctx.revert();
  }, []);

  // Animate selected date display
  useEffect(() => {
    if (selectedDate && selectedRef.current) {
      gsap.fromTo(
        selectedRef.current,
        { opacity: 0, y: 20, scale: 0.9 },
        { opacity: 1, y: 0, scale: 1, duration: 0.5, ease: "back.out(1.7)" },
      );
    }
  }, [selectedDate]);

  // Check if a date is available
  const isDateAvailable = (date) => {
    const dayName = format(date, "EEEE").toLowerCase();
    const businessHours = bookingConfig.businessHours[dayName];

    if (!businessHours) return false;
    if (isBefore(date, today)) return false;
    if (isBefore(maxDate, date)) return false;

    return true;
  };

  const handleDateChange = (date) => {
    if (isDateAvailable(date)) {
      setSelectedDate(date);
    }
  };

  const handleContinue = () => {
    if (selectedDate) {
      // Fade out animation before transition
      gsap.to(selectorRef.current, {
        opacity: 0,
        y: -20,
        duration: 0.3,
        ease: "power2.in",
        onComplete: () => onSelectDate(selectedDate),
      });
    }
  };

  const tileDisabled = ({ date }) => {
    return !isDateAvailable(date);
  };

  const tileClassName = ({ date }) => {
    if (!isDateAvailable(date)) return "calendar-tile--disabled";
    if (
      selectedDate &&
      format(date, "yyyy-MM-dd") === format(selectedDate, "yyyy-MM-dd")
    ) {
      return "calendar-tile--selected";
    }
    return "calendar-tile--available";
  };

  return (
    <div className="date-selector" ref={selectorRef}>
      <div className="date-selector__header" ref={headerRef}>
        <h2 className="date-selector__title">Select a Date</h2>
        <p className="date-selector__subtitle">
          Choose a day that works best for you
        </p>
      </div>

      <div className="date-selector__calendar-wrapper" ref={calendarRef}>
        <Calendar
          onChange={handleDateChange}
          value={selectedDate}
          minDate={today}
          maxDate={maxDate}
          tileDisabled={tileDisabled}
          tileClassName={tileClassName}
          locale="en-GB"
          showNeighboringMonth={false}
        />
      </div>

      {selectedDate && (
        <div className="date-selector__selected" ref={selectedRef}>
          <div className="date-selector__selected-date">
            Selected:{" "}
            <strong>{format(selectedDate, "EEEE, MMMM do, yyyy")}</strong>
          </div>
          <button
            className="date-selector__continue-btn"
            onClick={handleContinue}
          >
            Continue to Time Selection
          </button>
        </div>
      )}
    </div>
  );
};

export default DateSelector;
